// @ts-nocheck
import { useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Eye, EyeOff, Lock, User } from "lucide-react";
import type { LegalDocType } from "@kawkaw/shared";
import { useAdminLogin } from "../api/hooks";
import { useAuthStore } from "../store/auth";
import { LegalContentModal } from "../components/LegalContentModal";

interface LoginForm {
  email: string;
  password: string;
}

function FieldIcon({ type }: { type: "user" | "lock" }) {
  const Icon = type === "user" ? User : Lock;
  return <Icon className="h-4 w-4 text-slate-400" strokeWidth={2.4} aria-hidden="true" />;
}

export function LoginScreen() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const login = useAdminLogin();
  const { setTokens, setStaff } = useAuthStore();
  const [values, setValues] = useState<LoginForm>({ email: "", password: "" });
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [legalDoc, setLegalDoc] = useState<LegalDocType | null>(null);

  const updateValue = (field: keyof LoginForm, value: string) => {
    setValues((current) => ({ ...current, [field]: value }));
    if (error) setError("");
  };

  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!values.email.trim()) {
      setError(t("auth.emailRequired"));
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email.trim())) {
      setError(t("auth.emailInvalid"));
      return;
    }
    if (!values.password) {
      setError(t("auth.passwordRequired"));
      return;
    }

    try {
      const res = await login.mutateAsync(values);
      setTokens(res.data.tokens.accessToken, res.data.tokens.refreshToken);
      setStaff({
        id: res.data.admin.id,
        name: res.data.admin.name,
        role: res.data.admin.role,
        permissions: res.data.admin.permissions ?? [],
        kopitiamId: res.data.admin.kopitiam_id,
      });
      void navigate("/");
    } catch {
      setError(t("auth.loginErrorDetail"));
    }
  };

  return (
    <div className="admin-login flex min-h-screen items-center justify-center bg-slate-50 px-4 py-8 font-sans text-slate-900">
      <div className="flex min-h-[500px] w-full max-w-[820px] overflow-hidden rounded-[20px] bg-white shadow-elevated">
        <aside className="hidden w-[340px] shrink-0 flex-col justify-between bg-gradient-to-br from-slate-950 to-stone-900 p-10 md:flex">
          <div>
            <div className="mb-6 flex h-14 w-14 items-center justify-center rounded-2xl bg-brand text-xl font-black text-white">
              KK
            </div>
            <div className="mb-2 text-[22px] font-black text-white">{t("auth.brandName")}</div>
            <div className="text-sm leading-6 text-slate-300">{t("auth.brandTagline")}</div>
          </div>

          <div className="space-y-3">
            {[t("auth.featureOrder"), t("auth.featureStaff"), t("auth.featureReports"), t("auth.featureSettings")].map((text) => (
              <div key={text} className="flex items-center gap-3">
                <span className="h-2 w-2 rounded-full bg-brand" />
                <span className="text-sm font-semibold text-slate-300">{text}</span>
              </div>
            ))}
          </div>

          <div className="text-xs font-semibold text-slate-500">{t("auth.copyright")}</div>
        </aside>

        <main className="flex flex-1 flex-col justify-center p-8 sm:p-12">
          <div className="mb-9">
            <div className="mb-2 text-[26px] font-black text-slate-900">{t("auth.welcomeBack")}</div>
            <div className="text-sm font-medium text-slate-500">{t("auth.signInSubtitle")}</div>
          </div>

          <form onSubmit={onSubmit} autoComplete="off" className="space-y-5">
            <label className="block">
              <span className="mb-2 block text-[11px] font-black uppercase tracking-normal text-slate-500">{t("auth.emailLabel")}</span>
              <span className="admin-focus-wrap flex h-12 items-center gap-3 rounded-xl border border-slate-200 bg-white px-4 transition focus-within:border-brand/40 focus-within:ring-4 focus-within:ring-orange-100">
                <FieldIcon type="user" />
                <input
                  type="email"
                  value={values.email}
                  onChange={(event) => updateValue("email", event.target.value)}
                  placeholder={t("auth.emailPlaceholder")}
                  className="h-full min-w-0 flex-1 border-none bg-transparent text-sm font-semibold text-slate-900 outline-none placeholder:text-slate-400"
                />
              </span>
            </label>

            <label className="block">
              <span className="mb-2 block text-[11px] font-black uppercase tracking-normal text-slate-500">{t("auth.passwordLabel")}</span>
              <span className="admin-focus-wrap flex h-12 items-center gap-3 rounded-xl border border-slate-200 bg-white px-4 transition focus-within:border-brand/40 focus-within:ring-4 focus-within:ring-orange-100">
                <FieldIcon type="lock" />
                <input
                  type={showPassword ? "text" : "password"}
                  value={values.password}
                  onChange={(event) => updateValue("password", event.target.value)}
                  placeholder={t("auth.passwordPlaceholder")}
                  className="h-full min-w-0 flex-1 border-none bg-transparent text-sm font-semibold text-slate-900 outline-none placeholder:text-slate-400"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((current) => !current)}
                  className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-slate-400 transition hover:bg-slate-100 hover:text-slate-600"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  title={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" strokeWidth={2.4} /> : <Eye className="h-4 w-4" strokeWidth={2.4} />}
                </button>
              </span>
            </label>

            {error && (
              <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold text-red-700">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={login.isPending}
              className="flex h-[50px] w-full items-center justify-center rounded-xl bg-brand text-base font-extrabold text-white shadow-admin transition hover:bg-brand-dark disabled:cursor-not-allowed disabled:opacity-70"
            >
              {login.isPending ? t("common.loading", { defaultValue: "Loading..." }) : t("auth.signInBtn")}
            </button>
          </form>

          {import.meta.env.DEV && (
            <div className="mt-6 rounded-xl border border-orange-200 bg-orange-50 px-4 py-3 text-xs font-semibold leading-7 text-amber-900">
              <strong className="mb-1 block">{t("auth.demoCredentials")}</strong>
              <span className="font-mono">
                {t("auth.demoSuperLine", { email: "admin@kawkaw.my", password: "Admin@Kawkaw1" })}
              </span>
              <br />
              <span className="font-mono">
                {t("auth.demoAdminLine", { email: "admin2@kawkaw.my", password: "Admin@Kawkaw2" })}
              </span>
            </div>
          )}

          <div className="mt-6 flex justify-center gap-3 text-xs font-bold text-slate-400">
            <span className="cursor-pointer hover:text-orange-500" onClick={() => setLegalDoc("terms")}>{t("login.termsAndConditions", "Terms and Conditions")}</span>
            <span>·</span>
            <span className="cursor-pointer hover:text-orange-500" onClick={() => setLegalDoc("privacy")}>{t("login.privacyPolicy", "Privacy Policy")}</span>
          </div>
        </main>
      </div>
      <LegalContentModal docType={legalDoc} onClose={() => setLegalDoc(null)} />
    </div>
  );
}
